var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Mesh, PlaneGeometry, MeshBasicMaterial, Group, Texture, Matrix4 as ThreeMatrix4, SRGBColorSpace } from "three";
import { CollisionFacet, PrimitiveShape, InteractionFacet, SelectionHighlightFacet, InputEventTrigger, ProximitySphere, UpdateFacetOnModelMessage, hashCode, ScriptFacet, ScriptComposeFacet } from "flavr-common";
import { Vector2, Vector3 } from "flavr-math";
import { UpdateCollisionShapeLocalTransformMessage } from "@messages/selection";
import { ModelTouchState } from "@messages/interaction/InteractionMessages";
import { ScriptRPCOnlyHostMessage } from "@messages/scripting/ScriptMessages";
import { Renderable } from "@modules/rendering/renderable/Renderable";
import { myDomToImage } from "@modules/htmlCanvasRendering/myDomToImage";
import { inputEventService } from "@modules/inputs/input/InputEventService";
import { inputService } from "@modules/inputs/input/InputService";
import { modelPool } from "@modules/model";
import { showUI3dCursor, subscribeCursorChange } from "./UI3DModule";
const PLANE_GEOMETRY = new PlaneGeometry(1, 1);
const tmpVp = new Vector2();
const tmpEl = new Vector2();
const tmpMarkerVp = new Vector2();
const tmpMarkerEl = new Vector2();
const tmpControllerWorldPosition = new Vector3();
const tmpLocalV3 = new Vector3();
const HOVER_END_MS = 500;
/**
 * Renders a 3D HTML panel onto a `UI3DFacet` model as a live texture.
 *
 * HTML elements declare their event handlers via `data-event="methodName"`.
 * When the user interacts with such an element, `Ui3DRenderable` emits a
 * `ScriptRPCOnlyHostMessage` for every script attached to the same model.
 * The model script then tries to call a method with the same "methodName"
 *
 * Supported control types and their value passed to the method:
 * - Checkbox   → `boolean`
 * - Slider     → `number` 0–1
 * - Cycler     → `string` (current option label)
 * - Button     → no params
 *
 * Example HTML that specifies different inputs with functions that are then
 * called on the script of the same model:
 * ```html
 * <div class="ui3d-panel">
 *   <style>...</style>
 *   <input type="checkbox" data-event="onEnabledChanged" />
 *   <div data-role="slider-track" data-clickable="true" data-event="onIntensityChanged" data-value="0.5">
 *     <div data-role="slider-fill"></div>
 *     <div data-role="slider-thumb"></div>
 *   </div>
 *   <div class="ui3d-cycler">
 *     <div data-role="mode-prev" data-clickable="true" data-event="onModeChanged">&#9664;</div>
 *     <div data-role="mode-label" data-options="Mode A|Mode B|Mode C">Mode A</div>
 *     <div data-role="mode-next" data-clickable="true" data-event="onModeChanged">&#9654;</div>
 *   </div>
 *   <button data-event="onConfirm">Confirm</button>
 * </div>
 * ```
 *
 * A script on the same model has to defined these functions in order for them
 * to be called:
 * ```ts
 * class MyScript extends ModelBase {
 *   onEnabledChanged(value: boolean) { ... }
 *   onIntensityChanged(value: number) { ... }
 *   onModeChanged(value: string) { ... }
 *   onConfirm() { ... }
 * }
 * ```
 *
 * Scripts can also read and mutate the panel DOM directly via `getUiElement`.
 * Any change triggers an automatic texture re-render.
 * During proximity interaction, the cursor stays at the controller center
 * projected onto the panel; hit points still determine UI interaction targets.
 *
 * ```ts
 * onConfirm() {
 *   const btn = this.getUiElement("button[data-event='onConfirm']")
 *   if (btn !== undefined) btn.textContent = "Done!"
 *
 *   const label = this.getUiElement("#status-label")
 *   if (label !== undefined) label.style.display = "none"
 * }
 * ```
 */
export class UI3DRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.type = UI3DRenderable.typestatic;
        // Canvas geometry
        this.group = new Group();
        this.textureImageWidth = 0;
        this.textureImageHeight = 0;
        // Sizing
        this.renderedClientWidth = 1;
        this.renderedClientHeight = 1;
        this.width = 1;
        this.worldHeight = 1;
        // Rendering state
        this.isRendering = false;
        this.needsRerender = false;
        this.isCurrentlyVisible = true;
        this.isElementLoading = false;
        // Interaction
        this.invWorldMatrix = new ThreeMatrix4();
        this.isPressed = false;
        this.sliderDragActive = false;
        this.lastActivatedAt = 0;
        // Debugging
        this.SHOW_DEBUG_PLANE_COLOR = false;
        this.lastCrosshairRenderX = -Infinity;
        this.lastCrosshairRenderY = -Infinity;
        this.isTouchContactActive = false;
        this.isProximityContactActive = false;
        this.handleHover = (hitPoint, controllerUid) => {
            this.localToCoords(this.worldHitToLocal(hitPoint), tmpVp, tmpEl);
            this.resetHoverEndTimer();
            this.updateCrosshair(hitPoint, controllerUid);
            if (this.element === undefined)
                return;
            const target = this.deepestHoverable(this.elementsAtVp(tmpVp.x, tmpVp.y));
            if (target !== this.hoveredEl) {
                const init = { view: window, bubbles: true, cancelable: true, clientX: tmpVp.x, clientY: tmpVp.y };
                if (this.hoveredEl !== undefined) {
                    this.hoveredEl.dispatchEvent(new MouseEvent("mouseout", { view: window, bubbles: true, cancelable: true }));
                }
                this.hoveredEl = target;
                if (target !== undefined) {
                    target.dispatchEvent(new MouseEvent("mouseover", init));
                }
                requestAnimationFrame(() => { void this.scheduleRender(); });
            }
        };
        this.handleClick = (worldPoint, _localPoint) => {
            this.localToCoords(this.worldHitToLocal(worldPoint), tmpVp, tmpEl);
            if (this.element === undefined)
                return;
            const target = this.findTarget(tmpVp.x, tmpVp.y);
            if (target === undefined)
                return;
            this.activateElement(target, tmpVp.x, tmpVp.y);
            requestAnimationFrame(() => { void this.scheduleRender(); });
        };
        this.handleTouch = (state, hitPoint, controllerUid) => {
            this.localToCoords(this.worldHitToLocal(hitPoint), tmpVp, tmpEl);
            this.isTouchContactActive = state !== ModelTouchState.End;
            this.updateCrosshair(hitPoint, controllerUid);
            if (!this.isTouchContactActive && !showUI3dCursor && !this.isProximityContactActive)
                this.hideCrosshair();
            if (this.element === undefined)
                return;
            if (state === ModelTouchState.Start) {
                this.onPressStart();
                const target = this.findTarget(tmpVp.x, tmpVp.y);
                if ((target === null || target === void 0 ? void 0 : target.dataset.role) === "slider-track") {
                    this.sliderDragActive = true;
                    this.activeDragTrack = target;
                    target.dispatchEvent(new MouseEvent("mousemove", { view: window, bubbles: true, cancelable: true, clientX: tmpVp.x, clientY: tmpVp.y }));
                    this.emitUiBusEvent(target);
                    requestAnimationFrame(() => { void this.scheduleRender(); });
                }
                else if (target instanceof HTMLInputElement && target.type === "range") {
                    this.sliderDragActive = true;
                    this.moveRange(target, tmpVp.x);
                }
            }
            else if (state === ModelTouchState.Move) {
                if (this.sliderDragActive) {
                    if (this.activeDragTrack !== undefined) {
                        this.activeDragTrack.dispatchEvent(new MouseEvent("mousemove", { view: window, bubbles: true, cancelable: true, clientX: tmpVp.x, clientY: tmpVp.y }));
                        this.emitUiBusEvent(this.activeDragTrack);
                        requestAnimationFrame(() => { void this.scheduleRender(); });
                    }
                    else {
                        const target = this.findTarget(tmpVp.x, tmpVp.y);
                        if (target instanceof HTMLInputElement && target.type === "range") {
                            this.moveRange(target, tmpVp.x);
                        }
                    }
                }
            }
            else {
                this.onPressEnd();
                this.sliderDragActive = false;
                this.activeDragTrack = undefined;
                const target = this.findTarget(tmpVp.x, tmpVp.y);
                if (target === undefined)
                    return;
                this.activateElement(target, tmpVp.x, tmpVp.y);
                requestAnimationFrame(() => { void this.scheduleRender(); });
            }
        };
        this.onMutation = (mutations) => {
            if (this.crosshairEl !== undefined &&
                mutations.every((m) => m.target === this.crosshairEl || this.crosshairEl.contains(m.target)))
                return;
            void this.scheduleRender();
        };
        this.material = new MeshBasicMaterial({ transparent: true });
        this.mesh = new Mesh(PLANE_GEOMETRY, this.material);
        this.mesh.rotation.x = -Math.PI / 2;
        this.mesh.matrixAutoUpdate = false;
        this.mesh.updateMatrix();
        this.mesh.visible = false;
        this.group.add(this.mesh);
        this.group.matrixAutoUpdate = false;
        this.owner.addObjectToScene(this.group);
        const connection = this.owner.renderer.getConnection();
        connection.send(new UpdateFacetOnModelMessage(this.owner.modelUID, new SelectionHighlightFacet(false)));
        connection.sendMultiple([
            new UpdateFacetOnModelMessage(this.owner.modelUID, new CollisionFacet(PrimitiveShape.Box, true)),
            new UpdateCollisionShapeLocalTransformMessage(this.owner.modelUID, new ThreeMatrix4().makeScale(1, 0.01, 1).toArray()),
            new UpdateFacetOnModelMessage(this.owner.modelUID, new InteractionFacet({ behaviors: [new InputEventTrigger(true, true)] }))
        ]);
        inputEventService.register(this.owner.modelUID, this.facetType, this.handleClick, this.handleHover, this.handleTouch);
        this.unsubscribeCursorChange = subscribeCursorChange((visible) => {
            if (!visible && !this.isTouchContactActive && !this.isProximityContactActive)
                this.hideCrosshair();
            else if (visible && Number.isFinite(this.lastCrosshairRenderX) && Number.isFinite(this.lastCrosshairRenderY)) {
                this.showCrosshairAt(this.lastCrosshairRenderX, this.lastCrosshairRenderY);
            }
        });
    }
    /// Coordinate helpers ///
    localToCoords(localPoint, vp, el, snapOutsideToCenter = true) {
        if (this.element === undefined)
            return;
        const rect = this.element.getBoundingClientRect();
        // If the hit lands outside the panel bounds (e.g. proximity selection returns
        // a dummy world-origin hit point) fall back to the panel center so findTarget's
        // snap radius can still pick the nearest interactive element.
        const halfW = this.width / 2;
        const halfH = this.worldHeight / 2;
        const lx = snapOutsideToCenter && halfW > 0 && Math.abs(localPoint.x) > halfW ? 0 : localPoint.x;
        const lz = snapOutsideToCenter && halfH > 0 && Math.abs(localPoint.z) > halfH ? 0 : localPoint.z;
        el.set((lx / this.width + 0.5) * this.renderedClientWidth, (lz / this.worldHeight + 0.5) * this.renderedClientHeight);
        vp.set(el.x + rect.left, el.y + rect.top);
    }
    worldHitToLocal(hit) {
        return tmpLocalV3.set(hit.x, hit.y, hit.z).applyMatrix4(this.invWorldMatrix);
    }
    elementsAtVp(vx, vy) {
        const result = [];
        const traverse = (el) => {
            const r = el.getBoundingClientRect();
            if (vx >= r.left && vx <= r.right && vy >= r.top && vy <= r.bottom)
                result.push(el);
            for (const child of el.children)
                traverse(child);
        };
        if (this.element !== undefined)
            traverse(this.element);
        return result;
    }
    // Returns the checkbox or radio associated with a <label> element (via for= or implicit wrapping).
    labelTarget(label) {
        if (!(label instanceof HTMLLabelElement))
            return null;
        const forId = label.htmlFor;
        if (forId && this.element !== undefined) {
            const target = this.element.querySelector(`#${CSS.escape(forId)}`);
            if (target instanceof HTMLInputElement && (target.type === "checkbox" || target.type === "radio"))
                return target;
        }
        return label.querySelector('input[type="checkbox"], input[type="radio"]');
    }
    isInteractive(el) {
        const tag = el.tagName;
        if (tag === "LABEL")
            return this.labelTarget(el) !== null;
        return tag === "INPUT" || tag === "SELECT" || tag === "BUTTON" || tag === "TEXTAREA" || tag === "A" || el.dataset.clickable === "true";
    }
    deepestInteractive(els) {
        for (let i = els.length - 1; i >= 0; i--) {
            if (this.isInteractive(els[i]))
                return els[i];
        }
        return undefined;
    }
    // Like deepestInteractive but also matches elements that have author-defined
    // hover CSS (marked during rewritePseudoClassesToAttributes). Used by handleHover
    // so that containers such as .ui3d-radio-row receive mouseover/mouseout.
    deepestHoverable(els) {
        for (let i = els.length - 1; i >= 0; i--) {
            if (this.isInteractive(els[i]) || "ui3dCustomHover" in els[i].dataset)
                return els[i];
        }
        return undefined;
    }
    findTarget(vx, vy) {
        if (this.element === undefined)
            return undefined;
        const exact = this.deepestInteractive(this.elementsAtVp(vx, vy));
        if (exact !== undefined)
            return exact;
        const SNAP_PX = 80;
        let nearest;
        let nearestDist = SNAP_PX;
        for (const el of this.element.querySelectorAll('[data-clickable="true"], input, button, label')) {
            const r = el.getBoundingClientRect();
            const cx = (r.left + r.right) / 2;
            const cy = (r.top + r.bottom) / 2;
            const dist = Math.hypot(vx - cx, vy - cy);
            if (dist < nearestDist) {
                nearestDist = dist;
                nearest = el;
            }
        }
        return nearest;
    }
    /// Camera rotation lock ///
    onPressStart() {
        if (this.isPressed)
            return;
        this.isPressed = true;
        this.inputDisabledId = inputService.setDisabled();
    }
    onPressEnd() {
        if (!this.isPressed)
            return;
        this.isPressed = false;
        if (this.inputDisabledId !== undefined) {
            inputService.setEnabled(this.inputDisabledId);
            this.inputDisabledId = undefined;
        }
    }
    onHoverEnd() {
        this.onPressEnd();
        this.isProximityContactActive = false;
        if (!this.isTouchContactActive)
            this.hideCrosshair();
        if (this.hoveredEl !== undefined) {
            this.hoveredEl.dispatchEvent(new MouseEvent("mouseout", { view: window, bubbles: true, cancelable: true }));
            this.hoveredEl = undefined;
            requestAnimationFrame(() => { void this.scheduleRender(); });
        }
    }
    resetHoverEndTimer() {
        if (this.hoverEndTimer !== undefined)
            clearTimeout(this.hoverEndTimer);
        this.hoverEndTimer = setTimeout(() => {
            this.hoverEndTimer = undefined;
            this.onHoverEnd();
        }, HOVER_END_MS);
    }
    /// Script RPC emission ///
    // Checkbox → boolean; radio → string (value attr); slider-track → number in [min, max]; data-value → number if parseable, else string; others → undefined.
    resolveElementValue(el) {
        var _a, _b, _c;
        if (el instanceof HTMLInputElement && el.type === "checkbox")
            return el.checked;
        if (el instanceof HTMLInputElement && el.type === "radio")
            return el.value;
        if (el instanceof HTMLInputElement && el.type === "range")
            return parseFloat(el.value);
        if (el.dataset.role === "slider-track") {
            const min = parseFloat((_a = el.dataset.min) !== null && _a !== void 0 ? _a : "0");
            const max = parseFloat((_b = el.dataset.max) !== null && _b !== void 0 ? _b : "1");
            const t = parseFloat((_c = el.dataset.value) !== null && _c !== void 0 ? _c : "0.5");
            return min + t * (max - min);
        }
        const raw = el.dataset.value;
        if (raw === undefined)
            return undefined;
        const num = Number(raw);
        return isNaN(num) ? raw : num;
    }
    // Calls the named method on every script attached to this model.
    // Uses ScriptRPCOnlyHostMessage ("general") so the call stays local —
    // the script is responsible for any cross-peer replication it needs.
    // dispatchEvent is synchronous, so data-value is already updated by listeners when this runs.
    emitUiBusEvent(el) {
        const funcName = el.dataset.event;
        if (funcName === undefined)
            return;
        const funcNameHash = hashCode(funcName);
        const value = this.resolveElementValue(el);
        const params = value !== undefined ? [value] : [];
        const model = modelPool.getModel(this.owner.modelUID);
        if (model === undefined)
            return;
        const scriptIds = [];
        const scriptFacet = model.getFacet(ScriptFacet);
        if (scriptFacet !== undefined)
            scriptIds.push(scriptFacet.scriptId);
        const scriptComposeFacet = model.getFacet(ScriptComposeFacet);
        if (scriptComposeFacet !== undefined) {
            for (const info of scriptComposeFacet.scripts)
                scriptIds.push(info.scriptId);
        }
        for (const scriptId of scriptIds) {
            this.owner.renderer.pendingMessages.push(new ScriptRPCOnlyHostMessage(this.owner.modelUID, hashCode(scriptId), funcNameHash, params));
        }
    }
    /// Input event handlers ///
    showCrosshairAt(x, y) {
        const crosshair = this.crosshairEl;
        if (crosshair === undefined)
            return;
        const wasHidden = crosshair.style.display === "none";
        crosshair.setAttribute("style", `position:absolute;pointer-events:none;z-index:9999;left:${x}px;top:${y}px;`);
        const dx = x - this.lastCrosshairRenderX;
        const dy = y - this.lastCrosshairRenderY;
        if (wasHidden || dx * dx + dy * dy >= 36) {
            this.lastCrosshairRenderX = x;
            this.lastCrosshairRenderY = y;
            requestAnimationFrame(() => { void this.scheduleRender(); });
        }
    }
    hideCrosshair() {
        const crosshair = this.crosshairEl;
        if (crosshair === undefined || crosshair.style.display === "none")
            return;
        crosshair.setAttribute("style", "position:absolute;pointer-events:none;z-index:9999;display:none;");
        requestAnimationFrame(() => { void this.scheduleRender(); });
    }
    updateCrosshair(hitPoint, controllerUid) {
        const controllerModel = modelPool.getModel(controllerUid);
        this.isProximityContactActive = (controllerModel === null || controllerModel === void 0 ? void 0 : controllerModel.getFacet(ProximitySphere)) !== undefined;
        if (showUI3dCursor || this.isProximityContactActive || this.isTouchContactActive) {
            const markerLocal = this.isProximityContactActive && controllerModel !== undefined
                ? this.worldHitToLocal(controllerModel.getWorldPosition(tmpControllerWorldPosition))
                : this.worldHitToLocal(hitPoint);
            this.localToCoords(markerLocal, tmpMarkerVp, tmpMarkerEl, !this.isProximityContactActive);
            this.showCrosshairAt(tmpMarkerEl.x, tmpMarkerEl.y);
        }
    }
    moveRange(el, vx) {
        const rect = el.getBoundingClientRect();
        const t = rect.width > 0 ? Math.max(0, Math.min(1, (vx - rect.left) / rect.width)) : 0.5;
        const min = parseFloat(el.min) || 0;
        const max = parseFloat(el.max) || 100;
        el.value = String(min + t * (max - min));
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
        this.emitUiBusEvent(el);
        void this.scheduleRender();
    }
    // dispatchEvent is synchronous, so data-value is already updated when emitUiBusEvent runs.
    activateElement(el, vx, vy) {
        // Resolve label → its target input before the debounce check so that clicking
        // the label text and clicking the input directly share the same debounce bucket.
        // Without this, a touch-end + click pair arriving within 300 ms would each pass
        // the debounce (label !== input) and double-toggle a checkbox back to its original state.
        if (el instanceof HTMLLabelElement) {
            const input = this.labelTarget(el);
            if (input !== null)
                el = input;
        }
        const now = performance.now();
        if (el === this.lastActivatedEl && now - this.lastActivatedAt < 300)
            return;
        this.lastActivatedEl = el;
        this.lastActivatedAt = now;
        if (el instanceof HTMLInputElement && el.type === "checkbox") {
            el.checked = !el.checked;
            if (el.checked)
                el.setAttribute("checked", "");
            else
                el.removeAttribute("checked");
            el.dispatchEvent(new Event("change", { bubbles: true }));
            this.emitUiBusEvent(el);
            return;
        }
        if (el instanceof HTMLInputElement && el.type === "range") {
            this.moveRange(el, vx);
            return;
        }
        if (el instanceof HTMLInputElement && el.type === "radio") {
            if (el.checked)
                return;
            const name = el.name;
            if (name !== "") {
                for (const other of el.ownerDocument.querySelectorAll(`input[type="radio"][name="${name}"]`)) {
                    other.checked = false;
                    other.removeAttribute("checked");
                }
            }
            el.checked = true;
            el.setAttribute("checked", "");
            el.dispatchEvent(new Event("change", { bubbles: true }));
            this.emitUiBusEvent(el);
            void this.scheduleRender();
            return;
        }
        const init = { view: window, bubbles: true, cancelable: true, clientX: vx, clientY: vy };
        if (el.tagName === "BUTTON" || el instanceof HTMLButtonElement) {
            el.dispatchEvent(new MouseEvent("mousedown", init));
            void this.scheduleRender();
            setTimeout(() => {
                el.dispatchEvent(new MouseEvent("mouseup", init));
                el.dispatchEvent(new MouseEvent("click", init));
                this.emitUiBusEvent(el);
                void this.scheduleRender();
            }, 150);
            return;
        }
        el.dispatchEvent(new MouseEvent("click", init));
        this.emitUiBusEvent(el);
    }
    /// HTML loading ///
    loadHtmlAsset(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`/api/v1/asset/${id}/ui3d.html`);
            if (!response.ok)
                throw new Error(`Failed to load HTML asset "${id}": ${response.status}`);
            return response.text();
        });
    }
    // Fetches, mounts, and wires the element for the given asset ID.
    // Guards against stale responses if the facet's htmlAssetId changes mid-flight.
    loadAndSetElement(id) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isElementLoading = true;
            let html;
            try {
                html = yield this.loadHtmlAsset(id);
            }
            catch (e) {
                console.error("[Ui3DRenderable]", e);
                this.isElementLoading = false;
                return;
            }
            if (this.currentHtmlAssetId !== id || !this.isCurrentlyVisible) {
                this.isElementLoading = false;
                return;
            }
            this.element = this.createElementFromHtml(html);
            UI3DRenderable.elementRegistry.set(this.owner.modelUID, this.element);
            this.namespaceIdsAndNames(this.element);
            this.replaceRangeInputsWithCustomSliders(this.element);
            this.rewritePseudoClassesToAttributes(this.element);
            this.scopeCssToOwner(this.element);
            this.setupInteractiveElements(this.element);
            this.crosshairEl = this.createCrosshairEl();
            this.element.appendChild(this.crosshairEl);
            this.observer = new MutationObserver(this.onMutation);
            this.observer.observe(this.element, { attributes: true, childList: true, subtree: true, characterData: true });
            this.resizeObserver = new ResizeObserver(() => { void this.scheduleRender(); });
            this.resizeObserver.observe(this.element);
            this.isElementLoading = false;
            void this.scheduleRender();
        });
    }
    createElementFromHtml(html) {
        var _a;
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html.trim();
        const el = (_a = wrapper.firstElementChild) !== null && _a !== void 0 ? _a : wrapper;
        // The owner attribute lives on the panel root so it's present in the SVG
        // clone that myDomToImage renders — scoped CSS can then match without needing
        // a separate ancestor element.
        el.dataset.ui3dOwner = String(this.owner.modelUID);
        this.containerEl = document.createElement("div");
        this.containerEl.style.cssText = "position:fixed;left:-100000px;top:0;margin:0;contain:layout style paint;";
        this.containerEl.appendChild(el);
        document.body.appendChild(this.containerEl);
        return el;
    }
    namespaceIdsAndNames(el) {
        const prefix = `ui3d-${this.owner.modelUID}-`;
        // Only radio names need namespacing — two panels with the same name= would
        // share a radio group and clear each other's selection on click.
        // IDs are left as-is: labelTarget already uses a scoped querySelector so
        // global getElementById collisions are not a concern, and scripts query by
        // original IDs via getUiElement.
        for (const input of el.querySelectorAll('input[type="radio"][name]')) {
            input.name = prefix + input.name;
        }
    }
    // Scopes all CSS selectors inside <style> blocks within `el` by prepending
    // [data-ui3d-owner="<uid>"], preventing styles from leaking into other panels.
    // Selectors already prefixed with the owner selector are left untouched.
    // @-rules (e.g. @layer, @media) are not themselves scoped, but top-level rules
    // inside them are picked up by the regex anchored on the preceding `}`.
    scopeCssToOwner(el) {
        var _a;
        const ownerSelector = `[data-ui3d-owner="${this.owner.modelUID}"]`;
        for (const style of el.querySelectorAll("style")) {
            let css = (_a = style.textContent) !== null && _a !== void 0 ? _a : "";
            css = css.replace(/(^|})\s*([^@{}][^{]*)\{/g, (_match, close, selectorList) => {
                const scoped = selectorList
                    .split(",")
                    .map((s) => {
                    const selector = s.trim();
                    if (selector.length === 0)
                        return selector;
                    if (selector.startsWith(ownerSelector))
                        return selector;
                    // Descendant form: children of the panel root.
                    // Compound form (no space): the panel root element itself, which also
                    // carries data-ui3d-owner, so its own class rules apply in both real
                    // DOM and the SVG clone rendered by myDomToImage.
                    return `${ownerSelector} ${selector}, ${selector}${ownerSelector}`;
                })
                    .join(", ");
                return `${close} ${scoped} {`;
            });
            style.textContent = css;
        }
    }
    // Replaces <input type="range"> elements with the custom div-based slider
    // markup (data-role="slider-track/fill/thumb") so that CSS pseudo-elements
    // like ::-webkit-slider-runnable-track and ::-webkit-slider-thumb — which are
    // stripped by the SVG foreignObject renderer — can be remapped to styleable divs.
    replaceRangeInputsWithCustomSliders(container) {
        for (const range of Array.from(container.querySelectorAll('input[type="range"]'))) {
            const min = range.hasAttribute("min") ? parseFloat(range.getAttribute("min")) : 0;
            const max = range.hasAttribute("max") ? parseFloat(range.getAttribute("max")) : 1;
            const val = range.hasAttribute("value") ? parseFloat(range.getAttribute("value")) : (min + max) / 2;
            const t = max > min ? Math.max(0, Math.min(1, (val - min) / (max - min))) : 0.5;
            const pct = `${(t * 100).toFixed(1)}%`;
            const accentColor = getComputedStyle(range).accentColor;
            const track = document.createElement("div");
            track.dataset.role = "slider-track";
            track.dataset.clickable = "true";
            track.dataset.min = String(min);
            track.dataset.max = String(max);
            track.dataset.value = t.toFixed(3);
            if (range.dataset.event !== undefined)
                track.dataset.event = range.dataset.event;
            if (range.dataset.name !== undefined)
                track.dataset.name = range.dataset.name;
            if (accentColor && accentColor !== "auto")
                track.style.setProperty("--ui3d-accent", accentColor);
            const fill = document.createElement("div");
            fill.dataset.role = "slider-fill";
            fill.style.width = pct;
            const thumb = document.createElement("div");
            thumb.dataset.role = "slider-thumb";
            thumb.style.left = pct;
            track.appendChild(fill);
            track.appendChild(thumb);
            range.replaceWith(track);
        }
    }
    // Rewrites :hover/:active in the panel's own <style> blocks to data-attribute
    // selectors so JS can toggle them. CSS :hover never fires for synthetic mouse
    // events, but [data-ui3d-hover] toggled from JS works — and CSS transitions,
    // transforms, and filters all apply normally.
    // Elements that already have a :hover/:active rule are marked with
    // data-ui3d-custom-hover so the fallback defaults skip them entirely.
    rewritePseudoClassesToAttributes(el) {
        var _a, _b;
        // Detect author-defined :hover/:active rules by scanning THIS panel's own
        // <style> blocks — avoids cross-instance contamination from document.styleSheets.
        for (const style of el.querySelectorAll("style")) {
            const css = (_a = style.textContent) !== null && _a !== void 0 ? _a : "";
            const re = /([^{}]+)\{[^{}]*\}/g;
            let m;
            while ((m = re.exec(css)) !== null) {
                const selectorText = m[1];
                if (!selectorText.includes(":hover") && !selectorText.includes(":active"))
                    continue;
                const base = selectorText.replace(/:{1,2}(hover|active)/g, "").trim();
                if (!base)
                    continue;
                for (const s of base.split(",")) {
                    const trimmed = s.trim();
                    if (!trimmed)
                        continue;
                    try {
                        for (const target of el.querySelectorAll(trimmed)) {
                            target.dataset.ui3dCustomHover = "";
                        }
                    }
                    catch ( /* unparseable selector */_c) { /* unparseable selector */ }
                }
            }
        }
        for (const style of el.querySelectorAll("style")) {
            let css = ((_b = style.textContent) !== null && _b !== void 0 ? _b : "")
                .replace(/:hover/g, "[data-ui3d-hover]")
                .replace(/:active/g, "[data-ui3d-active]")
                .replace(/input\[type=["']range["']\]::?-webkit-slider-runnable-track/g, '[data-role="slider-track"]')
                .replace(/input\[type=["']range["']\]::?-webkit-slider-thumb/g, '[data-role="slider-thumb"]');
            // Strip webkit positioning/appearance properties from rewritten thumb rules.
            // background and border are driven by --ui3d-accent in the base defaults, so
            // carrying them over from the webkit rule would override the accent color and
            // add an unwanted border. border-radius, width, height, box-shadow are kept.
            css = css.replace(/(\[data-role="slider-thumb"\][^{]*\{)([\s\S]*?)(\})/g, (_, open, body, close) => open + body
                .replace(/[ \t]*margin-top\s*:[^;]*;?/g, "")
                .replace(/[ \t]*background\s*:[^;]*;?/g, "")
                .replace(/[ \t]*border\s*:[^;]*;?/g, "") // 'border:' but not 'border-radius:'
                + close);
            style.textContent = css;
        }
        // Inject default styles pre-scoped with the owner selector so scopeCssToOwner
        // (called after this) skips them. Structural rules are unlayered; hover/active
        // visual defaults go into @layer ui3d-defaults so any unlayered author rule beats
        // them in the cascade regardless of specificity.
        const ownerSelector = `[data-ui3d-owner="${this.owner.modelUID}"]`;
        const defaults = document.createElement("style");
        defaults.textContent = `
      ${ownerSelector} [data-role="slider-track"] { position: relative; width: 100%; min-height: 4px; cursor: pointer; display: block; box-sizing: border-box; overflow: visible; }
      ${ownerSelector} [data-role="slider-fill"]  { position: absolute; top: 0; left: 0; height: 100%; pointer-events: none; border-radius: inherit; background: var(--ui3d-accent, #4a90e2); }
      ${ownerSelector} [data-role="slider-thumb"] { position: absolute; top: 50%; transform: translate(-50%, -50%); margin: 0; pointer-events: none; width: 16px; height: 16px; border-radius: 50%; background: var(--ui3d-accent, #4a90e2); }
      @layer ui3d-defaults {
        ${ownerSelector} button[data-ui3d-hover]  { background: #e2e2e2; box-shadow: 0 4px 8px rgba(0,0,0,.25); }
        ${ownerSelector} button[data-ui3d-active] { background: #d4d4d4; box-shadow: 0 1px 2px rgba(0,0,0,.1); }
        ${ownerSelector} [data-role="slider-track"][data-ui3d-hover] { filter: brightness(1.15); }
        ${ownerSelector} [data-role="slider-thumb"][data-ui3d-hover] { transform: translate(-50%, -50%) scale(1.2); }
        ${ownerSelector} [data-role="mode-prev"][data-ui3d-hover],
        ${ownerSelector} [data-role="mode-next"][data-ui3d-hover] { background: #e0e0e0; }
        ${ownerSelector} input[type="checkbox"][data-ui3d-hover] { outline: 2px solid #4a90e2; }
      }
    `;
        el.prepend(defaults);
    }
    // Wires up interactive behaviours for widget types defined via data-role.
    // Toggles data-ui3d-hover / data-ui3d-active attributes on all interactive
    // elements; CSS rules (including user-defined ones) react to those attributes.
    setupInteractiveElements(container) {
        var _a;
        const setHover = (el, on) => { if (on)
            el.dataset.ui3dHover = "";
        else
            delete el.dataset.ui3dHover; };
        const setActive = (el, on) => { if (on)
            el.dataset.ui3dActive = "";
        else
            delete el.dataset.ui3dActive; };
        for (const track of container.querySelectorAll('[data-role="slider-track"]')) {
            const fill = track.querySelector('[data-role="slider-fill"]');
            const thumb = track.querySelector('[data-role="slider-thumb"]');
            const moveSlider = (clientX) => {
                const rect = track.getBoundingClientRect();
                const t = rect.width > 0 ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) : 0.5;
                const pct = `${(t * 100).toFixed(1)}%`;
                if (fill !== null)
                    fill.style.width = pct;
                if (thumb !== null)
                    thumb.style.left = pct;
                track.dataset.value = t.toFixed(3);
            };
            const isOverThumb = (clientX) => {
                var _a;
                if (thumb === null)
                    return false;
                const r = track.getBoundingClientRect();
                const thumbCenterX = r.left + parseFloat((_a = track.dataset.value) !== null && _a !== void 0 ? _a : "0.5") * r.width;
                return Math.abs(clientX - thumbCenterX) <= 10;
            };
            const updateSliderHover = (clientX) => {
                const over = isOverThumb(clientX);
                setHover(track, !over);
                if (thumb !== null)
                    setHover(thumb, over);
            };
            track.addEventListener("mouseover", (e) => { updateSliderHover(e.clientX); });
            track.addEventListener("mouseout", () => { setHover(track, false); if (thumb !== null)
                setHover(thumb, false); });
            track.addEventListener("mousemove", (e) => { moveSlider(e.clientX); updateSliderHover(e.clientX); });
            track.addEventListener("click", (e) => { moveSlider(e.clientX); });
        }
        for (const label of container.querySelectorAll('[data-role="mode-label"]')) {
            const options = ((_a = label.dataset.options) !== null && _a !== void 0 ? _a : "").split("|").filter(s => s.length > 0);
            if (options.length === 0)
                continue;
            let idx = 0;
            const prev = label.previousElementSibling;
            const next = label.nextElementSibling;
            const syncValue = () => {
                label.textContent = options[idx];
                if (prev !== null)
                    prev.dataset.value = options[idx];
                if (next !== null)
                    next.dataset.value = options[idx];
            };
            if (prev !== null) {
                prev.addEventListener("mouseover", () => { setHover(prev, true); });
                prev.addEventListener("mouseout", () => { setHover(prev, false); });
                prev.addEventListener("click", () => { idx = (idx - 1 + options.length) % options.length; syncValue(); });
            }
            if (next !== null) {
                next.addEventListener("mouseover", () => { setHover(next, true); });
                next.addEventListener("mouseout", () => { setHover(next, false); });
                next.addEventListener("click", () => { idx = (idx + 1) % options.length; syncValue(); });
            }
            syncValue();
        }
        for (const btn of container.querySelectorAll("button")) {
            btn.addEventListener("mouseover", () => { setHover(btn, true); });
            btn.addEventListener("mouseout", () => { setHover(btn, false); setActive(btn, false); });
            btn.addEventListener("mousedown", () => { setHover(btn, false); setActive(btn, true); });
            btn.addEventListener("mouseup", () => { setActive(btn, false); setHover(btn, true); });
        }
        for (const cb of container.querySelectorAll('input[type="checkbox"]')) {
            cb.addEventListener("mouseover", () => { setHover(cb, true); });
            cb.addEventListener("mouseout", () => { setHover(cb, false); });
        }
        for (const range of container.querySelectorAll('input[type="range"]')) {
            range.addEventListener("mouseover", () => { setHover(range, true); });
            range.addEventListener("mouseout", () => { setHover(range, false); });
        }
        // Generic hover for any element with author-defined :hover/:active CSS.
        // Buttons, sliders, and checkboxes above already have their own listeners,
        // but containers like .ui3d-radio-row or .ui3d-checkbox-row need this too.
        for (const el of container.querySelectorAll("[data-ui3d-custom-hover]")) {
            el.addEventListener("mouseover", () => { setHover(el, true); });
            el.addEventListener("mouseout", () => { setHover(el, false); setActive(el, false); });
            el.addEventListener("mousedown", () => { setActive(el, true); });
            el.addEventListener("mouseup", () => { setActive(el, false); });
        }
        container.addEventListener("change", () => { void this.scheduleRender(); }, true);
        container.addEventListener("input", () => { void this.scheduleRender(); }, true);
    }
    /// Rendering ///
    createCrosshairEl() {
        const el = document.createElement("div");
        el.setAttribute("style", "position:absolute;pointer-events:none;z-index:9999;display:none;");
        const dot = document.createElement("div");
        dot.setAttribute("style", "position:absolute;left:-6px;top:-6px;width:12px;height:12px;box-sizing:border-box;background:#39ff14;border:1px solid #003b00;box-shadow:0 0 4px rgba(0,0,0,0.9);border-radius:50%;");
        el.appendChild(dot);
        return el;
    }
    scheduleRender() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isRendering) {
                this.needsRerender = true;
                return;
            }
            do {
                this.needsRerender = false;
                yield this.renderToTexture();
            } while (this.needsRerender);
        });
    }
    renderToTexture() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.element === undefined)
                return;
            this.isRendering = true;
            let image;
            try {
                image = yield myDomToImage.toImage(this.element, this.cachedImage, {
                    bgColor: this.SHOW_DEBUG_PLANE_COLOR ? "red" : "transparent",
                    resolutionScale: 2,
                });
            }
            catch (e) {
                console.error("[Ui3DRenderable] render error", e);
                return;
            }
            finally {
                this.isRendering = false;
            }
            if (image === undefined)
                return;
            this.cachedImage = image;
            if (this.element === undefined)
                return;
            const imageW = image.naturalWidth;
            const imageH = image.naturalHeight;
            if (imageW <= 0 || imageH <= 0)
                return;
            const postW = this.element.clientWidth;
            const postH = this.element.clientHeight;
            const textureSizeChanged = imageW !== this.textureImageWidth || imageH !== this.textureImageHeight;
            if (this.texture === undefined || textureSizeChanged || this.texture.image !== image) {
                if (this.texture !== undefined)
                    this.texture.dispose();
                this.texture = new Texture(image);
                this.texture.colorSpace = SRGBColorSpace;
                this.texture.matrixAutoUpdate = false;
                this.material.map = this.texture;
                this.material.needsUpdate = true;
                this.mesh.visible = true;
                this.textureImageWidth = imageW;
                this.textureImageHeight = imageH;
            }
            this.texture.needsUpdate = true;
            const contentW = postW > 0 ? postW : this.renderedClientWidth;
            const contentH = postH > 0 ? postH : this.renderedClientHeight;
            this.renderedClientWidth = contentW;
            this.renderedClientHeight = contentH;
            this.applyLayout(imageH / imageW);
        });
    }
    applyLayout(aspectRatio) {
        this.worldHeight = this.width * aspectRatio;
        this.mesh.scale.set(this.width, this.worldHeight, 1);
        this.mesh.updateMatrix();
        this.group.matrixWorldNeedsUpdate = true;
        this.owner.renderer.getConnection().send(new UpdateCollisionShapeLocalTransformMessage(this.owner.modelUID, new ThreeMatrix4().makeScale(this.width, 0.01, this.worldHeight).toArray()));
    }
    /// Renderable interface ///
    updateFacet(facet) {
        var _a, _b, _c;
        this.width = facet.width;
        if (facet.htmlAssetId !== this.currentHtmlAssetId) {
            this.currentHtmlAssetId = facet.htmlAssetId;
            if (this.element !== undefined) {
                (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
                this.resizeObserver = undefined;
                (_b = this.observer) === null || _b === void 0 ? void 0 : _b.disconnect();
                this.observer = undefined;
                this.element.remove();
                this.element = undefined;
                (_c = this.containerEl) === null || _c === void 0 ? void 0 : _c.remove();
                this.containerEl = undefined;
                this.cachedImage = undefined;
                UI3DRenderable.elementRegistry.delete(this.owner.modelUID);
            }
            this.mesh.visible = false;
            if (facet.htmlAssetId !== "") {
                void this.loadAndSetElement(facet.htmlAssetId);
            }
        }
        void this.scheduleRender();
        requestAnimationFrame(() => { void this.scheduleRender(); });
        this.updateTransform();
        this.setVisible(this.owner.isVisible());
    }
    updateTransform() {
        this.group.matrix.copy(this.owner.matrixWorld);
        this.group.matrixWorldNeedsUpdate = true;
        this.invWorldMatrix.copy(this.owner.matrixWorld).invert();
    }
    setVisible(visible) {
        var _a, _b, _c, _d, _e;
        this.group.visible = visible;
        this.isCurrentlyVisible = visible;
        if (!visible) {
            this.onHoverEnd();
            this.crosshairEl = undefined;
            this.lastActivatedEl = undefined;
            (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
            this.resizeObserver = undefined;
            (_b = this.observer) === null || _b === void 0 ? void 0 : _b.disconnect();
            this.observer = undefined;
            (_c = this.element) === null || _c === void 0 ? void 0 : _c.remove();
            this.element = undefined;
            (_d = this.containerEl) === null || _d === void 0 ? void 0 : _d.remove();
            this.containerEl = undefined;
            this.cachedImage = undefined;
            (_e = this.texture) === null || _e === void 0 ? void 0 : _e.dispose();
            this.texture = undefined;
            this.material.map = null;
            this.material.needsUpdate = true;
            this.mesh.visible = false;
            UI3DRenderable.elementRegistry.delete(this.owner.modelUID);
        }
        else if (this.element === undefined && !this.isElementLoading && this.currentHtmlAssetId) {
            void this.loadAndSetElement(this.currentHtmlAssetId);
        }
    }
    destroy() {
        var _a, _b, _c, _d, _e;
        this.unsubscribeCursorChange();
        if (this.hoverEndTimer !== undefined) {
            clearTimeout(this.hoverEndTimer);
            this.hoverEndTimer = undefined;
        }
        this.onPressEnd();
        this.hoveredEl = undefined;
        this.crosshairEl = undefined;
        UI3DRenderable.elementRegistry.delete(this.owner.modelUID);
        inputEventService.unregister(this.owner.modelUID, this.facetType);
        (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        this.resizeObserver = undefined;
        (_b = this.observer) === null || _b === void 0 ? void 0 : _b.disconnect();
        this.observer = undefined;
        (_c = this.element) === null || _c === void 0 ? void 0 : _c.remove();
        this.element = undefined;
        (_d = this.containerEl) === null || _d === void 0 ? void 0 : _d.remove();
        this.containerEl = undefined;
        (_e = this.texture) === null || _e === void 0 ? void 0 : _e.dispose();
        this.texture = undefined;
        this.owner.removeObjectFromScene(this.group);
    }
}
UI3DRenderable.typestatic = "UI3DFacet";
UI3DRenderable.elementRegistry = new Map();
